import React, { Dispatch, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import * as Bixby from '$/core';
import dayjs from 'dayjs';
import { AnalyticsService } from '$/services';
import NoDataFound from '$/components/global/noData';
import { colorCode, leverageColorCode } from '$/pages/client/clo-analytics/heatMap';
import { ReactComponent as Padlock } from '$/assets/icons/padlock.svg';
import { GridApi } from 'ag-grid-enterprise';

interface IPotfolioGridProps {
  gridData?: Bixby.Models.CompanyPortfolio[];
  marketAnalytics?: Bixby.Models.CompanyAnalytics[];
  isAdminOrCreditSightsOpsSufOrRatings: boolean;
  hasPublicCompaniesFeatureFlag: boolean;
  hasBixbyComplianceFeatureFlag: boolean;
  isSyndicationPortfolioView?: boolean;
  hasUsEuroGroupingFeatureFlag: boolean;
  heatMapActive: boolean;
  setGridRef?: Dispatch<GridApi> | null;
  linkedGrid?: GridApi | null;
}

export const PortfolioGrid: React.FC<IPotfolioGridProps> = props => {
  const {
    gridData,
    marketAnalytics,
    isAdminOrCreditSightsOpsSufOrRatings,
    hasPublicCompaniesFeatureFlag,
    heatMapActive,
    hasBixbyComplianceFeatureFlag,
    isSyndicationPortfolioView,
    hasUsEuroGroupingFeatureFlag,
    setGridRef,
    linkedGrid,
  } = props;

  const topQuartileData = useMemo(() => {
    if (!marketAnalytics) return marketAnalytics;
    return marketAnalytics?.find(x => x.name[1] === 'Top Quartile' || x.name[2] === 'Top Quartile');
  }, [marketAnalytics]);

  const medianData = useMemo(() => {
    if (!marketAnalytics) return marketAnalytics;
    return marketAnalytics?.find(x => x.name[1] === 'Median' || x.name[2] === 'Median');
  }, [marketAnalytics]);

  const bottomQuartileData = useMemo(() => {
    if (!marketAnalytics) return marketAnalytics;
    return marketAnalytics?.find(x => x.name[1] === 'Bottom Quartile' || x.name[2] === 'Bottom Quartile');
  }, [marketAnalytics]);

  const spreadPercentageRegex = /^([0-9]+|[0-9]+[.][0-9]+)%$/g;
  const spreadRegex = /^([A-Za-z]\+([0-9]+|[0-9]+[.][0-9]+))$/g;
  const spreadAlphaRegex = /([^0-9.]+)?/g;

  const getContextMenuItems = (params: any) => {
    if (params.column.colId === 'uniqueName' || params.column.colId === 'companyName') {
      return [];
    }

    const result = ['copy', 'copyWithHeaders', 'separator', 'export'];
    return result;
  };

  const getCellClass = (
    params: any,
    heatMapActive: boolean,
    useLeverageColorCode: boolean,
    topQuartileDataValue?: number | null,
    medianQuartileDataValue?: number | null,
    bottomQuartileDataValue?: number | null
  ) => {
    if (
      topQuartileDataValue &&
      medianQuartileDataValue &&
      bottomQuartileDataValue &&
      params.node.key !== Bixby.Enums.AnalyticsMetrics.BottomDecile &&
      params.node.key !== Bixby.Enums.AnalyticsMetrics.TopDecile
    ) {
      if (useLeverageColorCode) {
        return leverageColorCode(
          heatMapActive,
          params,
          topQuartileDataValue,
          medianQuartileDataValue,
          bottomQuartileDataValue
        );
      } else {
        return colorCode(heatMapActive, params, topQuartileDataValue, medianQuartileDataValue, bottomQuartileDataValue);
      }
    } else return '';
  };

  const onGridReady = (params: any) => {
    if (setGridRef) {
      setGridRef(params.api || null);
    }
  };

  if (gridData && !Bixby.Utils.isNullOrEmpty(gridData)) {
    return (
      <React.Fragment>
        <AgGridReact
          enableRangeSelection={true}
          getContextMenuItems={getContextMenuItems}
          defaultColDef={{
            flex: 1,
            minWidth: 100,
            sortable: true,
            resizable: true,
            lockPinned: true,
            filter: 'agNumberColumnFilter',
            cellClass: 'align-center',
          }}
          rowData={gridData}
          suppressDragLeaveHidesColumns={true}
          suppressMovableColumns={true}
          animateRows={true}
          rowHeight={32}
          headerHeight={32}
          onGridReady={onGridReady}
          alignedGrids={[{ api: linkedGrid }]}
          columnDefs={[
            {
              hide: !isAdminOrCreditSightsOpsSufOrRatings,
              field: 'uniqueName',
              headerName: 'Company',
              minWidth: 275,
              pinned: 'left',
              cellClass: 'align-left',
              filter: 'agTextColumnFilter',
              floatingFilter: true,
              cellRenderer: (params: any) => {
                const url = '/company/' + params.data['companyId'];
                return (
                  <div className="company_contain">
                    <a className="link_underline" href={url}>
                      {' '}
                      {params.data['uniqueName']}
                    </a>
                    {hasBixbyComplianceFeatureFlag &&
                      params.data.declarationType === Bixby.Enums.DeclarationType.Private && (
                        <div className="private-company" role="presentation" title="Private Declaration">
                          <Padlock />
                        </div>
                      )}
                  </div>
                );
              },
              suppressColumnsToolPanel: !isAdminOrCreditSightsOpsSufOrRatings,
            },
            {
              hide: isAdminOrCreditSightsOpsSufOrRatings,
              field: 'companyName',
              headerName: 'Company',
              minWidth: 275,
              pinned: 'left',
              cellClass: 'align-left',
              filter: 'agTextColumnFilter',
              floatingFilter: true,
              cellRenderer: (params: any) => {
                return (
                  <div className="company_contain">
                    <a className="link_underline" href={`/company/${params.data['companyId']}`}>
                      {params.data['companyName']}
                    </a>{' '}
                    {hasBixbyComplianceFeatureFlag &&
                      params.data.declarationType === Bixby.Enums.DeclarationType.Private && (
                        <div className="private-company" role="presentation" title="Private Declaration">
                          <Padlock />
                        </div>
                      )}
                  </div>
                );
              },
              suppressColumnsToolPanel: isAdminOrCreditSightsOpsSufOrRatings,
            },
            {
              hide: !hasBixbyComplianceFeatureFlag,
              headerName: '',
              maxWidth: 50,
              resizable: false,
              sortable: false,
              suppressMenu: true,
              suppressColumnsToolPanel: true,
            },
            {
              headerName: 'Loan Ticker',
              field: 'openFigiTicker',
              minWidth: 130,
              cellClass: 'align-left',
              filter: 'agTextColumnFilter',
              floatingFilter: true,
            },
            {
              hide: !isSyndicationPortfolioView,
              headerName: 'Commitment Date',
              field: 'commitmentDate',
              minWidth: 170,
              cellClass: 'align-left',
              filter: 'agTextColumnFilter',
              valueGetter: params => {
                if (params.data?.['commitmentDate']) {
                  const commitmentDate: any = dayjs(params.data['commitmentDate'], { utc: true }).format('MM/DD/YYYY');
                  const currentDate: any = dayjs().format('MM/DD/YYYY');
                  const isExpired: boolean = new Date(commitmentDate) < new Date(currentDate);
                  return isExpired ? 'Expired' : commitmentDate;
                }
                return '';
              },
            },
            {
              hide: !isSyndicationPortfolioView,
              headerName: 'Transaction Types',
              field: 'transactionTypes',
              minWidth: 170,
              cellClass: 'align-left',
              filter: 'agTextColumnFilter',
              floatingFilter: true,
            },
            {
              hide: !hasPublicCompaniesFeatureFlag,
              headerName: 'Ownership',
              field: 'isSECPublic',
              minWidth: 130,
              cellClass: 'align-left',
              cellRenderer: (params: any) => (params.data['isSECPublic'] ? 'Public' : 'Private'),
              filter: 'agTextColumnFilter',
              filterParams: {
                valueGetter: (params: any) => (params.data['isSECPublic'] ? 'Public' : 'Private'),
              },
              suppressColumnsToolPanel: !hasPublicCompaniesFeatureFlag,
            },
            {
              hide: !hasUsEuroGroupingFeatureFlag,
              field: 'debtIssuance',
              filter: 'agSetColumnFilter',
              floatingFilter: true,
              headerName: 'Debt Issuance',
              minWidth: 250,
              cellClass: 'align-left',
              suppressColumnsToolPanel: !hasUsEuroGroupingFeatureFlag,
            },
            {
              field: 'industryGroupName',
              filter: 'agTextColumnFilter',
              floatingFilter: true,
              headerName: 'Industry Group',
              minWidth: 250,
              cellClass: 'align-left',
            },
            {
              field: 'asOfDate',
              headerName: 'As of,',
              valueGetter: params => {
                if (params.data?.['asOfDate']) {
                  return Bixby.Utils.formatMonthYear(dayjs.utc(params.data?.['asOfDate']).toDate()) as any;
                }
                return '';
              },
              minWidth: 100,
              filter: 'agTextColumnFilter',
            },
            {
              headerName: 'MyEBITDA Leverage',
              children: [
                {
                  hide: isAdminOrCreditSightsOpsSufOrRatings,
                  field: 'firstLienDebtGrossClientEbitda',
                  headerName: '1st Lien',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatMultiplier(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 130,
                  suppressColumnsToolPanel: isAdminOrCreditSightsOpsSufOrRatings,
                },
                {
                  hide: isAdminOrCreditSightsOpsSufOrRatings,
                  field: 'totalDebtGrossClientEbitda',
                  headerName: 'Total',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatMultiplier(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 130,
                  suppressColumnsToolPanel: isAdminOrCreditSightsOpsSufOrRatings,
                },
              ],
              suppressColumnsToolPanel: isAdminOrCreditSightsOpsSufOrRatings,
            },
            {
              headerName: 'EBITDA Leverage',
              cellClass: 'grid-cell-centered',
              children: [
                {
                  field: 'firstLienDebtGrossEbitda',
                  headerName: '1st Lien',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatMultiplier(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 130,
                  cellClass: (params: any) =>
                    getCellClass(
                      params,
                      heatMapActive,
                      true,
                      topQuartileData?.firstLienDebtGrossEbitda,
                      medianData?.firstLienDebtGrossEbitda,
                      bottomQuartileData?.firstLienDebtGrossEbitda
                    ),
                },
                {
                  field: 'totalDebtGrossEbitda',
                  headerName: 'Total',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatMultiplier(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 130,
                  cellClass: (params: any) =>
                    getCellClass(
                      params,
                      heatMapActive,
                      true,
                      topQuartileData?.totalDebtGrossEbitda,
                      medianData?.totalDebtGrossEbitda,
                      bottomQuartileData?.totalDebtGrossEbitda
                    ),
                },
              ],
            },
            {
              headerName: 'Adj. EBITDA Leverage',
              cellClass: 'grid-cell-centered',
              children: [
                {
                  field: 'firstLienDebtGrossAdjustedEbitda',
                  headerName: '1st Lien',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatMultiplier(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 120,
                  cellClass: (params: any) =>
                    getCellClass(
                      params,
                      heatMapActive,
                      true,
                      topQuartileData?.firstLienDebtGrossAdjustedEbitda,
                      medianData?.firstLienDebtGrossAdjustedEbitda,
                      bottomQuartileData?.firstLienDebtGrossAdjustedEbitda
                    ),
                },
                {
                  field: 'totalDebtGrossAdjustedEbitda',
                  headerName: 'Total',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatMultiplier(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 120,
                  cellClass: (params: any) =>
                    getCellClass(
                      params,
                      heatMapActive,
                      true,
                      topQuartileData?.totalDebtGrossAdjustedEbitda,
                      medianData?.totalDebtGrossAdjustedEbitda,
                      bottomQuartileData?.totalDebtGrossAdjustedEbitda
                    ),
                },
              ],
            },
            {
              headerName: 'Interest Coverage',
              cellClass: 'grid-cell-centered',
              children: [
                {
                  hide: isAdminOrCreditSightsOpsSufOrRatings,
                  field: 'interestCoverageClientEbitda',
                  headerName: 'MyEBITDA',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatMultiplier(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 130,
                  suppressColumnsToolPanel: isAdminOrCreditSightsOpsSufOrRatings,
                },
                {
                  field: 'interestCoverageEbitda',
                  headerName: 'EBITDA',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatMultiplier(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 130,
                  cellClass: (params: any) =>
                    getCellClass(
                      params,
                      heatMapActive,
                      false,
                      topQuartileData?.interestCoverageEbitda,
                      medianData?.interestCoverageEbitda,
                      bottomQuartileData?.interestCoverageEbitda
                    ),
                },
                {
                  field: 'interestCoverageAdjustedEbitda',
                  headerName: 'Adj. EBITDA',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatMultiplier(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 130,
                  cellClass: (params: any) =>
                    getCellClass(
                      params,
                      heatMapActive,
                      false,
                      topQuartileData?.interestCoverageAdjustedEbitda,
                      medianData?.interestCoverageAdjustedEbitda,
                      bottomQuartileData?.interestCoverageAdjustedEbitda
                    ),
                },
              ],
            },
            {
              headerName: 'Interest Coverage (After Capex)',
              cellClass: 'grid-cell-centered',
              children: [
                {
                  hide: isAdminOrCreditSightsOpsSufOrRatings,
                  field: 'interestCoverageAfterCapitalExpendituresClientEbitda',
                  headerName: 'MyEBITDA',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatMultiplier(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 130,
                  suppressColumnsToolPanel: isAdminOrCreditSightsOpsSufOrRatings,
                },
                {
                  field: 'interestCoverageAfterCapitalExpendituresEbitda',
                  headerName: 'EBITDA',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatMultiplier(params.value);
                    }
                    return 'NM';
                  },
                  cellClass: (params: any) =>
                    getCellClass(
                      params,
                      heatMapActive,
                      false,
                      topQuartileData?.interestCoverageAfterCapitalExpendituresEbitda,
                      medianData?.interestCoverageAfterCapitalExpendituresEbitda,
                      bottomQuartileData?.interestCoverageAfterCapitalExpendituresEbitda
                    ),
                  minWidth: 130,
                },
                {
                  field: 'interestCoverageAfterCapitalExpendituresAdjustedEbitda',
                  headerName: 'Adj. EBITDA',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatMultiplier(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 130,
                  cellClass: (params: any) =>
                    getCellClass(
                      params,
                      heatMapActive,
                      false,
                      topQuartileData?.interestCoverageAfterCapitalExpendituresAdjustedEbitda,
                      medianData?.interestCoverageAfterCapitalExpendituresAdjustedEbitda,
                      bottomQuartileData?.interestCoverageAfterCapitalExpendituresAdjustedEbitda
                    ),
                },
              ],
            },
            {
              headerName: 'Liquidity',
              cellClass: 'grid-cell-centered',
              children: [
                {
                  field: 'cash',
                  headerName: 'Cash',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatCurrency(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 130,
                },
                {
                  field: 'availableLiquidity',
                  headerName: 'Cash+Avail RC',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatCurrency(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 130,
                },
              ],
            },
            {
              headerName: 'LTM Financials',
              cellClass: 'grid-cell-centered',
              children: [
                {
                  field: 'revenue',
                  headerName: 'Revenue',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatCurrency(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 130,
                },
                {
                  hide: isAdminOrCreditSightsOpsSufOrRatings,
                  field: 'clientEbitda',
                  headerName: 'MyEBITDA',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatCurrency(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 130,
                  suppressColumnsToolPanel: isAdminOrCreditSightsOpsSufOrRatings,
                },
                {
                  field: 'ebitda',
                  headerName: 'EBITDA',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatCurrency(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 130,
                },
                {
                  field: 'adjustedEbitda',
                  headerName: 'Adj. EBITDA',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatCurrency(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 130,
                },
                {
                  field: 'ocf',
                  headerName: 'OCF',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatCurrency(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 130,
                },
                {
                  field: 'capitalExpendituresOcfCapEx',
                  headerName: 'OCF-Capex',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatCurrency(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 130,
                },
              ],
            },
            {
              headerName: 'MRQ Growth %',
              cellClass: 'grid-cell-centered',
              children: [
                {
                  field: 'revenueGrowth',
                  headerName: 'Revenue',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatPercentage(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 120,
                  cellClass: (params: any) =>
                    getCellClass(
                      params,
                      heatMapActive,
                      false,
                      topQuartileData?.revenueGrowth,
                      medianData?.revenueGrowth,
                      bottomQuartileData?.revenueGrowth
                    ),
                },
                {
                  field: 'ebitdaGrowth',
                  headerName: 'EBITDA',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatPercentage(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 120,
                  cellClass: (params: any) =>
                    getCellClass(
                      params,
                      heatMapActive,
                      false,
                      topQuartileData?.ebitdaGrowth,
                      medianData?.ebitdaGrowth,
                      bottomQuartileData?.ebitdaGrowth
                    ),
                },
                {
                  field: 'adjustedEbitdaGrowth',
                  headerName: 'Adj. EBITDA',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatPercentage(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 120,
                  cellClass: (params: any) =>
                    getCellClass(
                      params,
                      heatMapActive,
                      false,
                      topQuartileData?.adjustedEbitdaGrowth,
                      medianData?.adjustedEbitdaGrowth,
                      bottomQuartileData?.adjustedEbitdaGrowth
                    ),
                },
              ],
            },
            {
              headerName: 'Margin %',
              cellClass: 'grid-cell-centered',
              children: [
                {
                  field: 'ebitdaMargin',
                  headerName: 'EBITDA',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatPercentage(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 120,
                  cellClass: (params: any) =>
                    getCellClass(
                      params,
                      heatMapActive,
                      false,
                      topQuartileData?.ebitdaMargin,
                      medianData?.ebitdaMargin,
                      bottomQuartileData?.ebitdaMargin
                    ),
                },
                {
                  field: 'adjustedEbitdaMargin',
                  headerName: 'Adj. EBITDA',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatPercentage(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 120,
                  cellClass: (params: any) =>
                    getCellClass(
                      params,
                      heatMapActive,
                      false,
                      topQuartileData?.adjustedEbitdaMargin,
                      medianData?.adjustedEbitdaMargin,
                      bottomQuartileData?.adjustedEbitdaMargin
                    ),
                },
                {
                  field: 'ocfMargin',
                  headerName: 'OCF',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatPercentage(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 120,
                  cellClass: (params: any) =>
                    getCellClass(
                      params,
                      heatMapActive,
                      false,
                      topQuartileData?.ocfMargin,
                      medianData?.ocfMargin,
                      bottomQuartileData?.ocfMargin
                    ),
                },
                {
                  field: 'capitalExpendituresMargin',
                  headerName: 'OCF-Capex',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatPercentage(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 120,
                  cellClass: (params: any) =>
                    getCellClass(
                      params,
                      heatMapActive,
                      false,
                      topQuartileData?.capitalExpendituresMargin,
                      medianData?.capitalExpendituresMargin,
                      bottomQuartileData?.capitalExpendituresMargin
                    ),
                },
              ],
            },
            {
              headerName: '1st Lien Loan Details',
              cellClass: 'grid-cell-centered',
              children: [
                {
                  field: 'firstLien1Amount',
                  headerName: '$ Amt.',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatCurrency(params.value);
                    }
                    return 'NM';
                  },
                  minWidth: 120,
                  cellClass: (params: any) =>
                    getCellClass(
                      params,
                      heatMapActive,
                      false,
                      topQuartileData?.firstLien1Amount,
                      medianData?.firstLien1Amount,
                      bottomQuartileData?.firstLien1Amount
                    ),
                },
                {
                  field: 'firstLien1Spread',
                  headerName: 'Benchmark',
                  filter: 'agTextColumnFilter',
                  minWidth: 140,
                  valueFormatter: params => {
                    if (params.value) {
                      return params.value;
                    }
                    return 'NM';
                  },
                  valueGetter: params => {
                    let value: string | undefined = undefined;
                    try {
                      value = params?.data?.['firstLien1Spread'];

                      if (!value) {
                        return null;
                      }

                      const percentageMatched = value.match(spreadPercentageRegex);

                      if (percentageMatched) {
                        return 'Fixed';
                      } else {
                        const spreadMatched = value.match(spreadRegex);
                        if (spreadMatched) {
                          const spreadNumber = value.replace(spreadAlphaRegex, '');
                          const spreadPrefix = value.replace(spreadNumber, '');
                          return spreadPrefix;
                        }
                      }

                      return null;
                    } catch (e: any) {
                      AnalyticsService.exception(e);
                      AnalyticsService.event(Bixby.Constants.APPINSIGHTS_PORTFOLIO_SPREAD_INVALID, {
                        spread: value || '',
                      });
                      return null;
                    }
                  },
                },
                {
                  field: 'firstLien1Spread',
                  headerName: 'Spread',
                  filter: 'agTextColumnFilter',
                  minWidth: 120,
                  valueFormatter: params => {
                    if (params.value) {
                      return params.value;
                    }
                    return 'NM';
                  },
                  valueGetter: params => {
                    let value: string | undefined = undefined;
                    try {
                      value = params?.data?.['firstLien1Spread'];

                      if (!value) {
                        return null;
                      }

                      const percentageMatched = value.match(spreadPercentageRegex);

                      if (percentageMatched) {
                        const percentage = parseInt(value.replaceAll('%', '')) * 100;
                        return percentage;
                      } else {
                        const spreadMatched = value.match(spreadRegex);
                        if (spreadMatched) {
                          const spread = value.replace(spreadAlphaRegex, '');
                          return spread;
                        }
                      }

                      return null;
                    } catch (e: any) {
                      AnalyticsService.exception(e);
                      AnalyticsService.event(Bixby.Constants.APPINSIGHTS_PORTFOLIO_SPREAD_INVALID, {
                        spread: value || '',
                      });
                      return null;
                    }
                  },
                  cellClass: (params: any) =>
                    getCellClass(
                      params,
                      heatMapActive,
                      false,
                      topQuartileData?.firstLien1Spread,
                      medianData?.firstLien1Spread,
                      bottomQuartileData?.firstLien1Spread
                    ),
                },
                {
                  field: 'firstLien1Maturity',
                  headerName: 'Maturity',
                  valueFormatter: params => {
                    if (params.value) {
                      return Bixby.Utils.formatMonthYear(dayjs.utc(params.value).toDate()) as any;
                    }
                    return Bixby.Constants.MATURITY_EMPTY;
                  },
                  minWidth: 120,
                },
              ],
            },
          ]}></AgGridReact>
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        <section>
          <div className="portfolio_no_data">
            <NoDataFound dataType="companies" />
          </div>
        </section>
      </React.Fragment>
    );
  }
};
